/**
 * The Display bar — the row under the band on desk datasheets: "DISPLAY · Group: X ·
 * Sort: Y ↑" (canvas frame 1). The Group chip opens the full Display panel (frame 2,
 * P2): nested levels, per-level order, sort-within, filters, save-as-view. The Sort chip
 * keeps its small menu — it is a one-axis shortcut for what the panel also sets.
 *
 * Desk-only: rendered from the `!phone` branch of a screen; never shown at phone widths.
 */
import { useState, type ReactNode } from 'react';
import type { DisplayConfig, EntityDisplaySpec, SortDir } from './engine.js';
import { DisplayPanel } from './display-panel.js';
import { useDismiss } from './use-dismiss.js';

/** Re-exported for saved-views.tsx, which imported it from here before the hook moved to its own
 *  file (0.1.455: the panel's mini-menus needed it too, and the panel is imported BY this file). */
export { useDismiss } from './use-dismiss.js';

export function DisplayBar<Row>({
  spec,
  config,
  onChange,
  leading,
  panelFilters,
  onSaveView,
  children,
}: {
  spec: EntityDisplaySpec<Row>;
  config: DisplayConfig;
  onChange: (next: DisplayConfig) => void;
  /** Sits directly AFTER the Group/Sort chips (the frame's "+ Filter" spot). */
  leading?: ReactNode;
  /** The panel's Filters section content — the screen's own chips (frame 2). A render
   *  prop since 0.1.456: it receives `closePanel`, because a door opened from INSIDE the
   *  panel (the filter picker) must close the panel first — its scrim sat over the door
   *  otherwise, which is exactly what the maintainer saw ("opens up the old filter sheet
   *  behind the display sheet"). Same pattern as `onSaveView` below. */
  panelFilters?: (closePanel: () => void) => ReactNode;
  /** The panel's "Save as view…" act — the screen decides what it does. */
  onSaveView?: () => void;
  /** Right-aligned extras (Select · Save view · Print) — the screen's own. */
  children?: ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState<'group' | 'sort' | null>(null);
  const close = (): void => setOpen(null);
  const groupRef = useDismiss(open === 'group', close);
  const sortRef = useDismiss(open === 'sort', close);

  const groupLabel = config.levels.map((l) => spec.groupAxes.find((a) => a.id === l.axis)?.label ?? l.axis);
  const sortAxis = spec.sortAxes.find((a) => a.id === config.sortAxis);
  // The chip reads "None" until somebody picks — the list is still in its resting order
  // underneath (engine.ts `sortChosen`). Picking an axis OR flipping the direction is choosing.
  const sortChosen = config.sortChosen === true;

  const setSort = (axisId: string): void => {
    close();
    const axis = spec.sortAxes.find((a) => a.id === axisId);
    if (axis === undefined) return;
    onChange({ ...config, sortAxis: axisId, sortDir: axis.naturalDir, sortChosen: true });
  };

  const flipDir = (): void => {
    close();
    const next: SortDir = config.sortDir === 'asc' ? 'desc' : 'asc';
    onChange({ ...config, sortDir: next, sortChosen: true });
  };

  return (
    <div className="display-bar">
      <span className="display-bar-label">Display</span>

      <div className={open === 'group' ? 'display-chip-wrap display-chip-wrap-open' : 'display-chip-wrap'} ref={groupRef}>
        {/* The scrim (frame 2): everything but the panel and its chip dims while the
            panel is open. Fixed, below the wrap's raised z; a press on it closes.
            It lives INSIDE the dismiss ref, so it closes by its own handler. */}
        {open === 'group' && <div className="display-scrim" onPointerDown={close} />}
        <button
          type="button"
          className="chip display-chip"
          aria-haspopup="menu"
          aria-expanded={open === 'group'}
          onClick={() => setOpen(open === 'group' ? null : 'group')}
        >
          <span className="display-chip-key">Group:</span>
          {groupLabel.length === 0 ? (
            <strong>None</strong>
          ) : (
            groupLabel.map((label, i) => (
              <span key={label} className="display-chip-level">
                {i > 0 && (
                  <span className="display-chip-sep" aria-hidden="true">
                    ›
                  </span>
                )}
                <strong>{label}</strong>
              </span>
            ))
          )}
          <span className="display-chip-caret" aria-hidden="true">
            ⌄
          </span>
        </button>
        {open === 'group' && (
          <DisplayPanel
            spec={spec}
            config={config}
            onChange={onChange}
            filters={panelFilters?.(close)}
            /* The panel CLOSES before the screen's save door opens — its scrim sits above
               the save sheet otherwise (saved-views.spec caught the click intercepted). */
            {...(onSaveView !== undefined
              ? {
                  onSaveView: () => {
                    close();
                    onSaveView();
                  },
                }
              : {})}
          />
        )}
      </div>

      <div className="display-chip-wrap" ref={sortRef}>
        <button
          type="button"
          className="chip display-chip"
          aria-haspopup="menu"
          aria-expanded={open === 'sort'}
          onClick={() => setOpen(open === 'sort' ? null : 'sort')}
        >
          <span className="display-chip-key">Sort:</span>
          {sortChosen ? (
            <>
              <strong>{sortAxis?.label ?? config.sortAxis}</strong>
              <span aria-hidden="true">{config.sortDir === 'asc' ? '↑' : '↓'}</span>
            </>
          ) : (
            <strong>None</strong>
          )}
        </button>
        {open === 'sort' && (
          <div className="display-menu" role="menu" aria-label="Sort">
            {spec.sortAxes.map((axis) => (
              <button
                key={axis.id}
                type="button"
                role="menuitemradio"
                aria-checked={sortChosen && config.sortAxis === axis.id}
                className="display-menu-row"
                onClick={() => setSort(axis.id)}
              >
                {axis.label}
              </button>
            ))}
            <button type="button" role="menuitem" className="display-menu-row display-menu-dir" onClick={flipDir}>
              {config.sortDir === 'asc' ? 'Descending ↓' : 'Ascending ↑'}
            </button>
          </div>
        )}
      </div>

      {leading}
      <span className="display-bar-spacer" aria-hidden="true" />
      {children}
    </div>
  );
}
